import { useState } from 'react'
import { Modal } from '../../components/Modal'
import { clientesClient, type Client } from '../../services/clientesClient'
import { ApiClientError } from '../../services/apiClient'

type Props = {
  open: boolean
  client: Client | null
  onClose: () => void
  onEdit: () => void
  onDeleted: (id: number) => void
  canEdit?: boolean
}

/**
 * Modal de detalle del cliente (CLI-05).
 * Muestra todos los campos en sólo-lectura y permite invocar edición o eliminar.
 * canEdit=false oculta los botones de edición y eliminación (para técnicos).
 */
export function ClientDetailModal({ open, client, onClose, onEdit, onDeleted, canEdit = true }: Props) {
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!client) return null

  async function handleDelete() {
    if (!client) return
    setError(null)
    setDeleting(true)
    try {
      await clientesClient.remove(client.id)
      onDeleted(client.id)
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.apiError.message : 'No se pudo eliminar el cliente.',
      )
      setDeleting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={client.alias}
      description={client.legal_name}
      size="md"
      dismissible={!deleting}
      footer={
        <div className="modal__actions modal__actions--split">
          {canEdit && (
            <button
              type="button"
              className="danger-btn"
              onClick={() => setConfirmDelete(true)}
              disabled={deleting}
            >
              Eliminar
            </button>
          )}
          <div className="modal__actions">
            <button type="button" className="ghost-btn" onClick={onClose} disabled={deleting}>
              Cerrar
            </button>
            {canEdit && (
              <button type="button" className="primary-btn" onClick={onEdit} disabled={deleting}>
                Editar
              </button>
            )}
          </div>
        </div>
      }
    >
      <dl className="detail-dl">
        <dt>Alias</dt>
        <dd>{client.alias}</dd>

        <dt>Razón social</dt>
        <dd>{client.legal_name}</dd>

        <dt>RFC</dt>
        <dd className="mono">{client.tax_id}</dd>

        <dt>Domicilio</dt>
        <dd>{client.address ?? <span className="muted">—</span>}</dd>

        <dt>Planta</dt>
        <dd>{client.plant ?? <span className="muted">—</span>}</dd>

        <dt>Responsable de recepción</dt>
        <dd>{client.reception_contact ?? <span className="muted">—</span>}</dd>

        <dt>Correo electrónico</dt>
        <dd>
          {client.email
            ? <a href={`mailto:${client.email}`}>{client.email}</a>
            : <span className="muted">—</span>}
        </dd>

        <dt>Creado</dt>
        <dd className="muted">{client.created_at}</dd>

        <dt>Actualizado</dt>
        <dd className="muted">{client.updated_at}</dd>
      </dl>

      {error && <p className="error" style={{ marginTop: '1rem' }}>{error}</p>}

      {confirmDelete && (
        <Modal
          open={confirmDelete}
          onClose={() => !deleting && setConfirmDelete(false)}
          title="Eliminar cliente"
          description={`¿Eliminar "${client.alias}"? El cliente quedará archivado.`}
          size="sm"
          dismissible={!deleting}
          footer={
            <div className="modal__actions">
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="danger-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          }
        >
          <p className="muted">
            Esta acción no elimina la información del catálogo; solo deja de mostrarse en las listas
            y en el autocompletado. Los reportes históricos siguen referenciando al cliente.
          </p>
        </Modal>
      )}
    </Modal>
  )
}
